import { useEffect, useState, type CSSProperties } from 'react';
import { usePrefersReducedMotion } from '../../lib/motion';
import './flap.css';

import type { FlapTone } from './tones';

export type { FlapTone } from './tones';
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789€£';
const TICK_MS = 45;
/** Tile i settles after 4 + i ticks (the prototype's flipIn). */
const settlesAt = (i: number) => 4 + i;

export interface FlapProps {
  text: string;
  tone?: FlapTone;
  /** Play the flip-in reveal (once per mount / text change). Ignored under reduced motion. */
  animate?: boolean;
  /** Dark tiles on a light background (e.g. inside a white card). */
  onLight?: boolean;
  /** Font size of the glyphs; the tiles scale with it. */
  size?: number | string;
  className?: string;
  /** Called when the reveal has finished (immediately if not animating). */
  onSettled?: () => void;
}

/**
 * Split-flap text: one tile per character. The one loud device of the design — it
 * carries facts about the visitor's flight (status, money, claim reference).
 * Screen readers get the text once, via aria-label; the tiles are hidden from them.
 */
export function Flap({
  text,
  tone = 'amber',
  animate = false,
  onLight,
  size,
  className,
  onSettled,
}: FlapProps) {
  const reduced = usePrefersReducedMotion();
  const play = animate && !reduced;
  const chars = [...text];
  const [frame, setFrame] = useState<string[] | null>(null);

  // Like the prototype's flipIn: the final text shows first, then each tile rolls
  // through random glyphs and settles left to right.
  useEffect(() => {
    if (!play) {
      onSettled?.();
      return;
    }
    const target = [...text];
    const last = Math.max(0, ...target.map((_, i) => settlesAt(i)));
    let tick = 0;
    const t = setInterval(() => {
      tick += 1;
      if (tick >= last) {
        clearInterval(t);
        setFrame(null);
        onSettled?.();
        return;
      }
      setFrame(
        target.map((ch, i) =>
          ch === ' ' || tick >= settlesAt(i) ? ch : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]!,
        ),
      );
    }, TICK_MS);
    return () => {
      clearInterval(t);
      setFrame(null);
    };
    // onSettled is a notification; re-running the reveal when it changes identity would replay it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, play]);

  const shown = play && frame && frame.length === chars.length ? frame : chars;
  const style: CSSProperties | undefined = size !== undefined ? { fontSize: size } : undefined;
  const cls = ['flap', tone !== 'amber' && `flap--${tone}`, onLight && 'flap--onlight', className]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={cls} style={style} role="img" aria-label={text} data-flap={text}>
      {shown.map((ch, i) => (
        <i key={i} className={chars[i] === ' ' ? 'sp' : undefined} aria-hidden="true">
          {ch}
        </i>
      ))}
    </span>
  );
}
